export type ClientContact = {
  label: 'Email' | 'Phone' | 'Instagram';
  value: string;
  href: string | null;
};

/** Never turn arbitrary phone notes or extensions into a different number. */
export function phoneNumber(value: string | null | undefined) {
  const raw = value?.trim();
  if (!raw || /[\r\n]/.test(value!) || !/^\+?[\d ().-]+$/.test(raw)) return null;
  const number = raw.replace(/[ ().-]/g, '');
  const digits = number.startsWith('+')
    ? number.slice(1)
    : number.startsWith('00')
      ? number.slice(2)
      : number;
  return /^\d{3,15}$/.test(digits) ? number : null;
}

export function internationalPhone(value: string | null | undefined) {
  const number = phoneNumber(value);
  if (!number) return null;
  const digits = number.startsWith('+')
    ? number.slice(1)
    : number.startsWith('00')
      ? number.slice(2)
      : '';
  return /^[1-9]\d{6,14}$/.test(digits) ? digits : null;
}

export function instagramUrl(value: string | null | undefined) {
  const raw = value?.trim();
  if (!raw) return null;
  if (/^@?[a-zA-Z0-9_][a-zA-Z0-9_.]{0,29}$/.test(raw)) {
    return `https://www.instagram.com/${raw.replace(/^@/, '')}/`;
  }
  try {
    const url = new URL(/^((www\.)?instagram\.com)\//i.test(raw) ? `https://${raw}` : raw);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (
      !['instagram.com', 'www.instagram.com'].includes(url.hostname.toLowerCase()) ||
      url.username ||
      url.password ||
      url.port
    )
      return null;
    const handle = url.pathname.replace(/^\//, '').replace(/\/$/, '');
    if (!/^[a-zA-Z0-9_][a-zA-Z0-9_.]{0,29}$/.test(handle)) return null;
    return `https://www.instagram.com/${handle}/`;
  } catch {
    return null;
  }
}

export function clientContacts(value: {
  email: string | null;
  phone: string | null;
  instagram?: string | null;
}): ClientContact[] {
  const result: ClientContact[] = [];
  if (value.email?.trim())
    result.push({
      label: 'Email',
      value: value.email,
      href: /^[^\s@<>]+@[^\s@<>]+$/.test(value.email.trim())
        ? `mailto:${encodeURIComponent(value.email.trim())}`
        : null,
    });
  if (value.phone?.trim()) {
    const number = phoneNumber(value.phone);
    result.push({ label: 'Phone', value: value.phone, href: number ? `tel:${number}` : null });
  }
  if (value.instagram?.trim())
    result.push({
      label: 'Instagram',
      value: value.instagram,
      href: instagramUrl(value.instagram),
    });
  return result;
}
